import { Accepts, Types, OutletType } from "@citychallenge/common";
import { useScene } from "../../scene/use-scene";
import { Inlet, Outlet } from "../../reactive";
import { defineElement } from "../define-element";
import { POINT_SCALES } from "../../scene/points-event";
import { TransTokens } from "../../../i18n";

export class ChoiceProps {
  @Inlet(Accepts.Image) public headerImage?: Types["Image"];
  @Inlet(Accepts.MarkdownText) public introduction?: Types["MarkdownText"];
  @Inlet(Accepts.Text) public question?: Types["Text"];
  @Inlet(Accepts.ChoiceOptionList) public answers?: Types["ChoiceOptionList"];

  @Inlet(Accepts.ChoiceMode) public answerMode?: Types["ChoiceMode"];

  @Inlet(Accepts.ChoiceOptionLayout)
  public optionLayout?: Types["ChoiceOptionLayout"];

  @Inlet(Accepts.Boolean) public isRequired?: Types["Boolean"];

  // Outlets
  @Outlet() public onQuestionCorrect!: OutletType;
  @Outlet() public onQuestionIncorrect!: OutletType;
}

export const ChoiceDefinition = defineElement("choice", ChoiceProps, {
  versionRequired: "0.38.0",
});

export const choiceTokens = TransTokens.create({
  multipleCorrect: "There are multiple correct answers.",
});

type OptionState = "none" | "selected" | "correct" | "wrong" | "disabled";

export interface ChoiceSceneState {
  answered: string[];
}

export const useSharedChoiceHandler = (
  answers: Types["ChoiceOptionList"],
  answerMode?: Types["ChoiceMode"]
) => {
  const scene = useScene<ChoiceSceneState>();
  function onPressOption(option: Types["ChoiceOption"]) {
    const answered = scene.state.answered || [];

    scene.collectAction(option.id, true);
    if (answered.includes(option.id)) return;

    scene.collectEvent({
      event: "choice",
      value: option.id,
    });

    const newAnswered = [...answered, option.id];

    scene.setState({
      answered: newAnswered,
    });

    if (!option.correct) {
      if (answerMode === "any-wrong") {
        scene.addWrongAnswer(POINT_SCALES.wrongSingleAttempt);
      } else {
        scene.addWrongAnswer();
      }
    }

    if (answerMode === "any-wrong") {
      // If any wrong answer was given stop the scene.
      if (!option.correct) {
        scene.lifecycle.onAnswer();
        return;
      }
    }

    if (answerMode === "any-correct") {
      if (option.correct) {
        scene.lifecycle.onAnswer();
        return;
      }
    }

    const correctAnswers = answers.filter((answer) => !!answer.correct);
    const givenCorrect = correctAnswers.filter(({ id }) =>
      newAnswered.includes(id)
    );

    if (givenCorrect.length === correctAnswers.length)
      scene.lifecycle.onAnswer();
  }
  function getOptionState(option: Types["ChoiceOption"]): OptionState {
    const answered = scene.state.answered || [];

    if (answered.includes(option.id))
      return option.correct ? "correct" : "wrong";

    if (scene.isAnswered) {
      if (answerMode === "any-correct") return "disabled";

      return option.correct ? "correct" : "disabled";
    }

    return "none";
  }

  return {
    onPressOption,
    getOptionState,
    needsMultipleAnswers:
      answerMode !== "any-correct" &&
      answers.filter((answer) => !!answer.correct).length >= 2,
  };
};
